import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Upload, FileSpreadsheet, X, CheckCircle2, AlertCircle, Trash2,
  Edit3, ChevronRight, Layers, Package, Lightbulb, Save,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";
import { normalizeDateInput } from "./FinanceLedgerSection";

const ENTITY_OPTIONS = [
  { value: "tasks", label: "Project tasks" },
  { value: "milestones", label: "Milestones (M1, M2 …)" },
  { value: "external_packages", label: "External package items" },
  { value: "planning_assumptions", label: "Planning assumptions" },
  { value: "skip", label: "Skip — do not import" },
];

const TASK_FIELDS = [
  { key: "name", label: "Activity / task" },
  { key: "phase", label: "Phase" },
  { key: "lead_party", label: "Lead party" },
  { key: "risk", label: "Risk" },
  { key: "start_date", label: "Start date" },
  { key: "finish_date", label: "Finish date" },
  { key: "working_days", label: "Working days" },
  { key: "notes", label: "Notes" },
  { key: "status", label: "Status (optional)" },
  { key: "progress", label: "Progress % (optional)" },
];
const PACKAGE_FIELDS = [
  { key: "activity", label: "Package activity" },
  { key: "package", label: "Package / supplier" },
  { key: "start_date", label: "Start date" },
  { key: "finish_date", label: "Finish date" },
  { key: "working_days", label: "Working days" },
  { key: "trigger", label: "Required before / trigger" },
  { key: "criticality", label: "Criticality" },
  { key: "notes", label: "Notes" },
];
const ASSUMPTION_FIELDS = [
  { key: "planning_item", label: "Planning item" },
  { key: "date_basis", label: "Date / basis" },
  { key: "lead_interface", label: "Lead / interface" },
  { key: "programme_intent", label: "Programme intent" },
  { key: "source_note", label: "Source / note" },
  { key: "status", label: "Status" },
];
const MILESTONE_FIELDS = [
  { key: "code", label: "Code (M1, M2 …)" },
  { key: "name", label: "Milestone name" },
  { key: "start_date", label: "Start date" },
  { key: "target_date", label: "Target date" },
  { key: "status", label: "Status" },
  { key: "progress", label: "Progress % (optional)" },
  { key: "responsibility", label: "Responsibility (optional)" },
];
const FIELDS_BY_ENTITY = {
  tasks: TASK_FIELDS,
  milestones: MILESTONE_FIELDS,
  external_packages: PACKAGE_FIELDS,
  planning_assumptions: ASSUMPTION_FIELDS,
  skip: [],
};

function fmtCell(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v.length > 80 ? v.slice(0, 78) + "…" : v;
  if (typeof v === "object" && v.__isoformat !== undefined) return v.__isoformat;
  return String(v);
}

export default function GanttUploadWizard({ slug, open, onClose, onImported, initialFile = null }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [tables, setTables] = useState([]); // editable copy with entity/column_map per table
  const [replace, setReplace] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [replaceSummary, setReplaceSummary] = useState(null);
  const [diffResult, setDiffResult] = useState(null);
  const fileName = file?.name || initialFile?.name || "";

  // Any change to mapping / replace-mode invalidates a prior dry-run review.
  useEffect(() => { setDiffResult(null); }, [tables, replace]);

  useEffect(() => {
    if (open && preview && !replaceSummary) {
      portalApi.ganttReplaceSummary(slug).then(setReplaceSummary).catch(() => null);
    }
  }, [open, preview, replaceSummary, slug]);

  const reset = useCallback(() => {
    setFile(null); setPreview(null); setTables([]); setErr("");
    setImportResult(null); setBusy(false); setDiffResult(null);
  }, []);

  useEffect(() => { if (!open) reset(); }, [open, reset]);

  // If the wizard is opened with a pre-attached file (e.g. straight from
  // "Create project & parse programme"), kick off the preview immediately.
  useEffect(() => {
    if (!open || !initialFile || preview || busy) return;
    setFile(initialFile);
    (async () => {
      setBusy(true); setErr("");
      try {
        const data = await portalApi.previewGantt(slug, initialFile);
        setPreview(data);
        const flat = [];
        (data.sheets || []).forEach((sh) => {
          (sh.tables || []).forEach((t) => {
            flat.push({
              id: t.id, sheet: sh.name, header_row: t.header_row,
              row_count: t.row_count, headers: t.headers || [],
              entity: t.entity === "unknown" ? "skip" : t.entity,
              column_map: { ...(t.column_map || {}) },
              rows: t.rows || t.sample_rows || [],
            });
          });
        });
        setTables(flat);
      } catch (e) { setErr(e.message); }
      finally { setBusy(false); }
    })();
  }, [open, initialFile, slug, busy, preview]);

  if (!open) return null;

  const choose = (e) => {
    setErr("");
    const f = e.target.files?.[0];
    setFile(f || null);
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const data = await portalApi.previewGantt(slug, file);
      setPreview(data);
      // Flatten tables across sheets for the mapping UI
      const flat = [];
      (data.sheets || []).forEach((sh) => {
        (sh.tables || []).forEach((t) => {
          flat.push({
            id: t.id,
            sheet: sh.name,
            header_row: t.header_row,
            row_count: t.row_count,
            headers: t.headers || [],
            entity: t.entity === "unknown" ? "skip" : t.entity,
            column_map: { ...(t.column_map || {}) },
            rows: t.rows || t.sample_rows || [],
          });
        });
      });
      setTables(flat);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const setEntity = (idx, ent) => {
    setTables((ts) => ts.map((t, i) => i === idx ? { ...t, entity: ent } : t));
  };
  const setMapping = (idx, field, colIdx) => {
    setTables((ts) => ts.map((t, i) =>
      i === idx ? { ...t, column_map: { ...t.column_map, [field]: colIdx === "" ? undefined : Number(colIdx) } } : t
    ));
  };

  const runImport = async () => {
    setBusy(true); setErr("");
    try {
      const sheets = tables.map((t) => ({
        entity: t.entity,
        column_map: t.column_map,
        sheet: t.sheet,
        rows: t.rows,
      }));
      // Priority 1/2: dry-run diff + validation BEFORE committing.
      if (!diffResult) {
        const d = await portalApi.ganttDiff(slug, { replace_existing: replace, sheets });
        setDiffResult(d);
        setBusy(false);
        if (!d.can_commit) {
          setErr("Import blocked — resolve the validation errors listed below.");
        }
        return;
      }
      if (!diffResult.can_commit) {
        setErr("Import blocked — resolve the validation errors listed below.");
        setBusy(false);
        return;
      }
      const result = await portalApi.importGantt(slug, {
        replace_existing: replace, sheets, confirm_warnings: true, filename: fileName || undefined,
      });
      setImportResult(result);
      onImported && onImported();
    } catch (e) {
      // Surface structured validation/confirmation errors from 422/409.
      const detail = e.detail || e.data?.detail;
      if (detail && (detail.errors || detail.warnings)) {
        setDiffResult((prev) => ({
          ...(prev || {}),
          validation: { errors: detail.errors || [], warnings: detail.warnings || [] },
          destructive: detail.destructive ?? (prev?.destructive),
          diff: detail.diff || prev?.diff,
          can_commit: !(detail.errors || []).length,
        }));
      }
      setErr((detail && detail.message) || e.message);
    }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto">
      <div className="bg-white w-full max-w-[1100px] border border-[#6E1F2C]/20 p-7 md:p-10 max-h-[94vh] overflow-y-auto" data-testid="portal-gantt-wizard">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">Programme</p>
            <h2 className="font-serif-display text-2xl text-[#1C110C]">Upload Gantt / Project Execution Excel</h2>
            <p className="text-xs text-[#7A6B5F] mt-1">Supports .xlsx / .xlsm. Each detected table can be mapped to tasks, package items or planning assumptions.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
        </div>

        {importResult ? (
          <div data-testid="portal-gantt-import-result">
            <div className="flex items-center gap-3 mb-5">
              <CheckCircle2 className="text-[#6E1F2C]" size={28} />
              <h3 className="font-serif-display text-xl text-[#1C110C]">Programme imported</h3>
            </div>
            <ul className="space-y-2 text-sm text-[#3E2A22]">
              <li><strong>{importResult.imported.phases}</strong> phases</li>
              <li><strong>{importResult.imported.tasks}</strong> tasks</li>
              {importResult.imported.milestones > 0 && (
                <li><strong>{importResult.imported.milestones}</strong> milestones synced</li>
              )}
              <li><strong>{importResult.imported.external_packages}</strong> external package items</li>
              <li><strong>{importResult.imported.planning_assumptions}</strong> planning assumptions</li>
              {importResult.skipped_sheets > 0 && (
                <li><strong>{importResult.skipped_sheets}</strong> sheet{importResult.skipped_sheets === 1 ? "" : "s"} skipped (not imported)</li>
              )}
            </ul>
            {importResult.safeguard && (
              <p className="text-[11px] text-[#6E1F2C] mt-4 border border-[#6E1F2C]/15 bg-[#FAF5EC] p-3" data-testid="portal-gantt-safeguard">
                ✓ {importResult.safeguard}
              </p>
            )}
            <button onClick={() => { onClose(); }} data-testid="portal-gantt-import-done" className="w-full mt-7 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F]">
              Done — view timeline
            </button>
          </div>
        ) : !preview ? (
          <div>
            <label className="border-2 border-dashed border-[#6E1F2C]/30 hover:border-[#6E1F2C] block p-10 text-center cursor-pointer transition-colors">
              <FileSpreadsheet className="mx-auto text-[#6E1F2C] mb-3" size={42} />
              <p className="text-sm text-[#1C110C]">{file ? file.name : "Click to select a .xlsx workbook"}</p>
              {file && <p className="text-xs text-[#7A6B5F] mt-1">{(file.size / 1024).toFixed(0)} KB</p>}
              <input data-testid="portal-gantt-file-input" type="file" accept=".xlsx,.xlsm,.xls" onChange={choose} className="hidden" />
            </label>
            {err && <p data-testid="portal-gantt-error" className="text-xs text-[#A3141A] mt-4">{err}</p>}
            <button
              type="button"
              onClick={runPreview}
              disabled={!file || busy}
              data-testid="portal-gantt-preview-btn"
              className="w-full mt-6 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Upload size={14} /> {busy ? "Parsing workbook…" : "Parse and preview"}
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#3E2A22]">
                Parsed <strong>{tables.length}</strong> table{tables.length === 1 ? "" : "s"} across{" "}
                <strong>{preview.sheets.length}</strong> sheet{preview.sheets.length === 1 ? "" : "s"}.
              </p>
              <label className="text-xs text-[#3E2A22] flex items-center gap-2 cursor-pointer">
                <input data-testid="portal-gantt-replace-toggle" type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="accent-[#6E1F2C]" />
                Replace existing programme on import
              </label>
            </div>
            <div className="space-y-5">
              {tables.map((t, idx) => {
                const fields = FIELDS_BY_ENTITY[t.entity] || [];
                return (
                  <div key={t.id} data-testid={`portal-gantt-table-${idx}`} className="border border-[#6E1F2C]/15 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">{t.sheet} · row {t.header_row}</p>
                        <p className="text-sm text-[#1C110C]">{t.row_count} rows · {t.headers.filter(Boolean).length} columns</p>
                      </div>
                      <select
                        value={t.entity}
                        onChange={(e) => setEntity(idx, e.target.value)}
                        data-testid={`portal-gantt-entity-${idx}`}
                        className="bg-[#FAF5EC] border border-[#6E1F2C]/25 px-3 py-2 text-xs text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                      >
                        {ENTITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>

                    {fields.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {fields.map((f) => (
                          <div key={f.key}>
                            <label className="block text-[9px] tracking-[0.25em] uppercase text-[#7A6B5F] mb-1">{f.label}</label>
                            <select
                              value={t.column_map[f.key] ?? ""}
                              onChange={(e) => setMapping(idx, f.key, e.target.value)}
                              data-testid={`portal-gantt-map-${idx}-${f.key}`}
                              className="w-full bg-white border border-[#6E1F2C]/20 px-2 py-1.5 text-xs text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
                            >
                              <option value="">— skip —</option>
                              {t.headers.map((h, i) => (h && String(h).trim()) ? (
                                <option key={i} value={i}>Col {i + 1}: {String(h).slice(0, 40)}</option>
                              ) : null)}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}

                    <details>
                      <summary className="cursor-pointer text-xs text-[#6E1F2C] hover:underline">Show sample rows ({Math.min(t.rows.length, 5)})</summary>
                      <div className="mt-3 overflow-x-auto">
                        <table className="text-[11px] text-[#1C110C] border border-[#6E1F2C]/10">
                          <thead className="bg-[#FAF5EC]">
                            <tr>{t.headers.slice(0, 8).map((h, i) => (
                              <th key={i} className="px-2 py-1 text-left border-b border-[#6E1F2C]/10 max-w-[140px] truncate">{String(h || "").slice(0, 30)}</th>
                            ))}</tr>
                          </thead>
                          <tbody>
                            {t.rows.slice(0, 5).map((r) => (
                              <tr key={r.row} className="border-b border-[#6E1F2C]/5">
                                {r.cells.slice(0, 8).map((c, i) => (
                                  <td key={i} className="px-2 py-1 max-w-[180px] truncate">{fmtCell(c)}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </div>
                );
              })}
            </div>

            {err && <p data-testid="portal-gantt-error" className="text-xs text-[#A3141A] mt-4">{err}</p>}

            <div className="mt-6 border border-[#A6822F]/40 bg-[#FAF5EC] p-4 text-[11px] text-[#3E2A22] space-y-1" data-testid="portal-gantt-replace-warning">
              <p className="text-[10px] tracking-[0.24em] uppercase text-[#A6822F]">Before you confirm</p>
              {replace && replaceSummary && (
                <p>
                  <strong>{replaceSummary.existing?.tasks ?? 0}</strong> existing project tasks,{" "}
                  <strong>{replaceSummary.existing?.phases ?? 0}</strong> phases,{" "}
                  <strong>{replaceSummary.existing?.external_packages ?? 0}</strong> external package items and{" "}
                  <strong>{replaceSummary.existing?.planning_assumptions ?? 0}</strong> planning assumptions will be replaced.
                </p>
              )}
              <p>
                <strong>{tables.filter((t) => t.entity !== "skip").length}</strong> mapped table{tables.filter((t) => t.entity !== "skip").length === 1 ? "" : "s"} will be imported ·{" "}
                <strong>{tables.filter((t) => t.entity === "skip").length}</strong> sheet{tables.filter((t) => t.entity === "skip").length === 1 ? "" : "s"} marked &quot;Skip&quot; will be completely ignored.
              </p>
              <p className="text-[#6E1F2C] font-medium">
                Existing daily updates, photos, reports, comments and audit history will not be modified or deleted by this update.
              </p>
            </div>

            {diffResult && (
              <div className="mt-5 border border-[#6E1F2C]/20 bg-[#FAF5EC]/50 p-4" data-testid="portal-gantt-diff-review">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C] mb-3">Change preview</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {Object.entries(diffResult.diff || {}).map(([ent, d]) => (
                    <div key={ent} className="border border-[#6E1F2C]/15 bg-white px-3 py-2" data-testid={`portal-gantt-diff-${ent}`}>
                      <p className="text-[10px] uppercase tracking-wide text-[#7A6B5F]">{ent.replace("_", " ")}</p>
                      <p className="text-xs text-[#1C110C] mt-1">
                        <span className="text-[#3F6E2C]">+{d.added_count || 0}</span>{" · "}
                        <span className="text-[#6E1F2C]">~{d.updated_count || 0}</span>{" · "}
                        <span className="text-[#A3141A]">-{d.removed_count || 0}</span>
                      </p>
                    </div>
                  ))}
                </div>
                {(diffResult.validation?.errors || []).length > 0 && (
                  <div className="mb-2" data-testid="portal-gantt-diff-errors">
                    <p className="text-[11px] font-semibold text-[#A3141A] uppercase tracking-wide mb-1">Errors (must fix)</p>
                    <ul className="text-xs text-[#A3141A] list-disc pl-5 space-y-0.5">
                      {diffResult.validation.errors.map((e, i) => (
                        <li key={i}>{e.sheet ? `${e.sheet} ` : ""}{e.row ? `row ${e.row}: ` : ""}{e.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(diffResult.validation?.warnings || []).length > 0 && (
                  <div data-testid="portal-gantt-diff-warnings">
                    <p className="text-[11px] font-semibold text-[#A6822F] uppercase tracking-wide mb-1">Warnings</p>
                    <ul className="text-xs text-[#7A6B5F] list-disc pl-5 space-y-0.5">
                      {diffResult.validation.warnings.map((w, i) => (
                        <li key={i}>{w.sheet ? `${w.sheet} ` : ""}{w.row ? `row ${w.row}: ` : ""}{w.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {diffResult.destructive && (
                  <p className="mt-2 text-xs text-[#A3141A]" data-testid="portal-gantt-diff-destructive">
                    This REPLACE will remove existing programme rows. Confirm to proceed.
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => { setPreview(null); setTables([]); setFile(null); setDiffResult(null); }}
                className="flex-1 border border-[#6E1F2C]/30 text-[#6E1F2C] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC]"
              >
                ← Choose different file
              </button>
              <button
                type="button"
                onClick={runImport}
                disabled={busy || (diffResult && !diffResult.can_commit)}
                data-testid="portal-gantt-import-btn"
                className="flex-1 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-50"
              >
                {busy ? "Working…" : !diffResult ? "Review changes" : diffResult.can_commit ? "Confirm import" : "Fix errors to import"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ====== TimelineTab — read view + edit-in-place for admin/PM/site ======
const TASK_STATUSES = ["not_started", "in_progress", "completed", "delayed", "on_hold", "awaiting_approval"];
const STATUS_LABEL = {
  not_started: "Not started", in_progress: "In progress", completed: "Completed",
  delayed: "Delayed", on_hold: "On hold", awaiting_approval: "Awaiting approval",
};
const STATUS_CHIP = {
  not_started: "bg-[#7A6B5F] text-white",
  in_progress: "bg-[#6E1F2C] text-white",
  completed: "bg-[#3F6E2C] text-white",
  delayed: "bg-[#A3141A] text-white",
  on_hold: "bg-[#1C110C] text-white",
  awaiting_approval: "bg-[#C8A75D] text-[#1C110C]",
};
const PACKAGE_STATUS_OPTIONS = [
  ["", "— status —"],
  ["not_ordered", "Not ordered"],
  ["ordered", "Ordered"],
  ["paid", "Paid"],
  ["partially_paid", "Partially paid"],
  ["in_manufacturing", "In manufacturing"],
  ["in_transit", "In transit"],
  ["partially_received", "Partially received"],
  ["received", "Received"],
  ["installed", "Installed"],
  ["closed", "Closed"],
  ["on_hold", "On hold"],
  ["cancelled", "Cancelled"],
];
const PACKAGE_STATUS_LABEL = Object.fromEntries(PACKAGE_STATUS_OPTIONS);
const PACKAGE_DONE = new Set(["received", "installed", "closed"]);

const RISK_CHIP = {
  high: "bg-[#A3141A] text-white",
  medium: "bg-[#C8A75D] text-[#1C110C]",
  low: "bg-[#3F6E2C] text-white",
};

function PhaseCard({ phase, tasks, canEdit, onEditTask, onDeleteTask }) {
  const total = tasks.length;
  const done = tasks.filter((t) => t.status === "completed").length;
  const inProg = tasks.filter((t) => t.status === "in_progress").length;
  const delayed = tasks.filter((t) => t.status === "delayed").length;
  const progress = total ? Math.round((done / total) * 100) : 0;
  const demo = phase.demolition_display || null;
  const fw = demo?.forecast_window || null;
  return (
    <div
      className="bg-white border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/30 transition-colors overflow-hidden"
      data-testid={`portal-timeline-phase-${phase.id}`}
    >
      <div className="px-5 py-4 border-b border-[#6E1F2C]/10 bg-gradient-to-r from-[#FAF5EC] to-white">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] font-medium">Phase</p>
            <h3 className="font-serif-display text-lg text-[#1C110C] leading-tight mt-0.5">{phase.name}</h3>
          </div>
          <div className="text-right">
            {demo ? (
              <div data-testid={`portal-phase-demo-aggregate-${phase.id}`}>
                <p className="font-serif-display text-2xl text-[#6E1F2C] leading-none">
                  {demo.percent != null ? `${demo.percent}%` : "—"}
                </p>
                <p className="text-[10px] tracking-[0.14em] uppercase text-[#7A6B5F] mt-1 max-w-[280px]">
                  {demo.percent != null
                    ? "measured (approved weights)"
                    : "Aggregate unresolved — pending verified weights / measurement"}
                </p>
              </div>
            ) : (
              <>
                <p className="font-serif-display text-2xl text-[#6E1F2C] leading-none">{progress}%</p>
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F] mt-1">{done}/{total} done</p>
              </>
            )}
          </div>
        </div>
        {(!demo || demo.percent != null) && (
          <div className="mt-3 h-1.5 bg-[#FAF5EC] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D4A963] via-[#A6822F] to-[#6E1F2C] rounded-full transition-all duration-500"
              style={{ width: `${demo && demo.percent != null ? demo.percent : progress}%` }}
            />
          </div>
        )}
        {demo && (
          <div className="mt-3 space-y-1" data-testid={`portal-phase-demo-components-${phase.id}`}>
            {(demo.owner_components || []).map((c, i) => (
              <p key={i} className="text-[11px] text-[#3E2A22] leading-snug">
                <span className="text-[#6E1F2C] font-medium">{c.label}:</span> {c.value}
              </p>
            ))}
            {demo.detail && <p className="text-[10px] text-[#7A6B5F]">{demo.detail}</p>}
            {fw && (
              <p className="text-[11px] text-[#6E1F2C] mt-1" data-testid={`portal-phase-demo-forecast-${phase.id}`}>
                <span className="inline-block px-1.5 py-0.5 bg-[#6E1F2C]/10 border border-[#6E1F2C]/25 tracking-[0.14em] uppercase text-[8px] mr-1.5">Owner forecast</span>
                internal finish {fw.start ? `${fw.start} – ` : "by "}{fw.end}
                {fw.baseline_end ? ` · baseline ${fw.baseline_end} retained` : ""}
              </p>
            )}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {inProg > 0 && (
            <span className="text-[9px] tracking-[0.22em] uppercase bg-[#6E1F2C] text-white px-2 py-0.5">
              {inProg} in progress
            </span>
          )}
          {delayed > 0 && (
            <span className="text-[9px] tracking-[0.22em] uppercase bg-[#A3141A] text-white px-2 py-0.5">
              {delayed} delayed
            </span>
          )}
          {!demo && done === total && total > 0 && (
            <span className="text-[9px] tracking-[0.22em] uppercase bg-[#3F6E2C] text-white px-2 py-0.5">
              Complete
            </span>
          )}
        </div>
      </div>
      <ul className="divide-y divide-[#6E1F2C]/5">
        {tasks.length === 0 ? (
          <li className="px-5 py-4 text-xs text-[#7A6B5F] italic">No tasks in this phase yet.</li>
        ) : tasks.map((t) => {
          const pct = Math.max(0, Math.min(100, t.progress_percent || (t.status === "completed" ? 100 : 0)));
          const riskKey = (t.risk || "").toLowerCase();
          return (
            <li key={t.id} data-testid={`portal-timeline-task-${t.id}`} className="px-5 py-3 hover:bg-[#FAF5EC]/40">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#1C110C] leading-snug">
                    {t.name}
                    {t.category && t.category !== "contractor_physical" && (
                      <span className="ml-2 align-middle text-[8px] tracking-[0.16em] uppercase px-1.5 py-0.5 bg-[#A6822F]/15 text-[#A6822F] border border-[#A6822F]/30" data-testid={`portal-task-category-${t.id}`}>
                        {{ authority_approval: "Authority approval", client_supplier: "Client supplier",
                           property_management: "Property mgmt", planning_assumption: "Planning",
                           financial_milestone: "Payment" }[t.category] || t.category}
                      </span>
                    )}
                  </p>
                  {t.status === "delayed" && t.delay_category && t.delay_category !== "contractor" && (
                    <p className="text-[10px] text-[#A6822F] mt-0.5">External approval dependency{t.delay_reason ? ` — ${t.delay_reason}` : ""}</p>
                  )}
                  <p className="text-[11px] text-[#7A6B5F] mt-1">
                    {t.lead_party ? `${t.lead_party} · ` : ""}
                    {t.start_date || "—"} → {t.finish_date || "—"}
                    {t.working_days ? ` · ${t.working_days} WD` : ""}
                  </p>
                  {t.forecast_end_date && t.status !== "completed" && (
                    <p className="text-[10px] text-[#6E1F2C] mt-0.5 flex items-center gap-1" data-testid={`portal-task-forecast-${t.id}`}>
                      <span className="inline-block px-1.5 py-0.5 bg-[#6E1F2C]/10 border border-[#6E1F2C]/25 tracking-[0.14em] uppercase text-[8px]">Owner forecast</span>
                      finish {t.forecast_end_date} · baseline {t.finish_date || "—"} retained
                    </p>
                  )}
                  {t.starts_after_internal_demolition && (
                    <p className="text-[10px] text-[#A6822F] mt-0.5" data-testid={`portal-task-sequence-${t.id}`}>
                      Starts after internal demolition completion (owner sequence)
                    </p>
                  )}
                  {t.scope_status === "deferred" && (
                    <p className="text-[10px] text-[#7A6B5F] mt-0.5" data-testid={`portal-task-deferred-${t.id}`}>
                      Deferred — off current critical path (not cancelled)
                    </p>
                  )}
                  {t.computed_rollup_percent != null && canEdit && (
                    <p className="text-[10px] text-[#7A6B5F] mt-0.5" data-testid={`portal-task-summary-note-${t.id}`}>
                      {t.auto_summary ? "Auto-grouped as summary" : "Summary row"} — excluded from weighted totals
                      (computed rollup {t.computed_rollup_percent}%). Stored value untouched; set "Individual activity" in Edit to include it.
                    </p>
                  )}
                  {t.notes && <p className="text-[11px] text-[#3E2A22] mt-1 italic line-clamp-2">{t.notes}</p>}
                  {(t.progress_percent != null || t.status === "in_progress" || t.status === "completed") && (
                    <div className="mt-2 max-w-[260px]">
                      <div className="h-1 bg-[#FAF5EC] rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            t.status === "completed" ? "bg-[#3F6E2C]" : "bg-gradient-to-r from-[#D4A963] to-[#6E1F2C]"
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F] mt-0.5">{pct}% complete</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {t.risk && (
                    <span className={`text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 inline-flex items-center gap-1 ${RISK_CHIP[riskKey] || "bg-[#7A6B5F] text-white"}`}>
                      <AlertCircle size={9} /> {t.risk}
                    </span>
                  )}
                  <span className={`text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 ${STATUS_CHIP[t.status] || STATUS_CHIP.not_started}`}>
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                  {canEdit && (
                    <>
                      <button type="button" onClick={() => onEditTask(t)} data-testid={`portal-timeline-edit-task-${t.id}`} className="p-1.5 border border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors"><Edit3 size={12} /></button>
                      <button type="button" onClick={() => onDeleteTask(t)} data-testid={`portal-timeline-delete-task-${t.id}`} className="p-1.5 border border-[#A3141A]/40 text-[#A3141A] hover:bg-[#A3141A] hover:text-white transition-colors"><Trash2 size={12} /></button>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EditTaskModal({ task, onClose, onSaved, slug }) {
  const [form, setForm] = useState({
    name: task.name || "", lead_party: task.lead_party || "",
    start_date: task.start_date || "", finish_date: task.finish_date || "",
    working_days: task.working_days ?? "", status: task.status || "not_started",
    progress_percent: task.progress_percent ?? 0, risk: task.risk || "",
    category: task.category || "", delay_category: task.delay_category || "",
    is_summary: "",
    delay_reason: task.delay_reason || "",
    notes: task.notes || "", visibility: task.visibility || "public",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      // DOM-first date reads: some native pickers update input[type=date]
      // without firing React onChange, leaving state stale (Publish 48 bug).
      const startEl = e.target.querySelector('[data-testid="portal-task-edit-start-date"]');
      const finishEl = e.target.querySelector('[data-testid="portal-task-edit-finish-date"]');
      const rawStart = (startEl && startEl.value) || form.start_date || "";
      const rawFinish = (finishEl && finishEl.value) || form.finish_date || "";
      payload.start_date = rawStart ? normalizeDateInput(rawStart) : "";
      payload.finish_date = rawFinish ? normalizeDateInput(rawFinish) : "";
      if (rawStart && !payload.start_date) throw new Error("Start date must be a valid date (YYYY-MM-DD).");
      if (rawFinish && !payload.finish_date) throw new Error("Finish date must be a valid date (YYYY-MM-DD).");
      payload.working_days = payload.working_days === "" ? null : Number(payload.working_days);
      payload.progress_percent = Number(payload.progress_percent || 0);
      if (payload.is_summary === "") delete payload.is_summary;
      else payload.is_summary = payload.is_summary === "true";
      // strip empty strings to nulls
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const data = await portalApi.patchTask(slug, task.id, payload);
      onSaved(data.task);
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const cls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";
  const lbl = "block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1";
  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto">
      <form onSubmit={submit} data-testid="portal-timeline-task-edit-form" className="bg-white w-full max-w-[640px] border border-[#6E1F2C]/20 p-7 md:p-9">
        <div className="flex items-start justify-between mb-5">
          <h3 className="font-serif-display text-xl text-[#1C110C]">Edit task</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={lbl}>Name</label><input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} required className={cls} data-testid="portal-task-edit-name" /></div>
          <div><label className={lbl}>Lead party</label><input value={form.lead_party} onChange={(e) => setForm((s) => ({ ...s, lead_party: e.target.value }))} className={cls} /></div>
          <div><label className={lbl}>Risk</label>
            <select value={form.risk} onChange={(e) => setForm((s) => ({ ...s, risk: e.target.value }))} className={cls}>
              <option value="">—</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
          <div><label className={lbl}>Start date</label><input type="date" value={form.start_date || ""} onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))} className={cls} data-testid="portal-task-edit-start-date" /></div>
          <div><label className={lbl}>Finish date</label><input type="date" value={form.finish_date || ""} onChange={(e) => setForm((s) => ({ ...s, finish_date: e.target.value }))} className={cls} data-testid="portal-task-edit-finish-date" /></div>
          <div><label className={lbl}>Working days</label><input type="number" min="0" value={form.working_days} onChange={(e) => setForm((s) => ({ ...s, working_days: e.target.value }))} className={cls} /></div>
          <div><label className={lbl}>Status</label>
            <select data-testid="portal-task-edit-status" value={form.status} onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))} className={cls}>
              {TASK_STATUSES.map((st) => <option key={st} value={st}>{STATUS_LABEL[st]}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Progress %</label><input type="number" min="0" max="100" value={form.progress_percent} onChange={(e) => setForm((s) => ({ ...s, progress_percent: e.target.value }))} className={cls} /></div>
          <div><label htmlFor="task-row-type" className={lbl}>Progress row type</label>
            <select id="task-row-type" data-testid="portal-task-edit-row-type" value={form.is_summary} onChange={(e) => setForm((s) => ({ ...s, is_summary: e.target.value }))} className={cls}>
              <option value="">Keep current: {task.is_summary === true ? "Summary row" : task.is_summary === false ? "Individual activity" : "Automatic"}</option>
              <option value="false">Individual activity</option>
              <option value="true">Summary / parent row</option>
            </select>
            <p className="text-xs text-[#7A6B5F] mt-1">Individual activities count toward progress. Summary rows roll up their child activities.</p>
          </div>
          <div><label className={lbl}>Work category</label>
            <select data-testid="portal-task-edit-category" value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} className={cls}>
              <option value="">Auto-classify</option>
              <option value="contractor_physical">Contractor physical work</option>
              <option value="authority_approval">Third-party authority approval</option>
              <option value="client_supplier">Client supplier / nominated vendor</option>
              <option value="property_management">Property management / building approval</option>
              <option value="planning_assumption">Planning assumption</option>
              <option value="financial_milestone">Financial / payment milestone</option>
            </select>
          </div>
          <div><label className={lbl}>Delay attribution</label>
            <select data-testid="portal-task-edit-delay-category" value={form.delay_category} onChange={(e) => setForm((s) => ({ ...s, delay_category: e.target.value }))} className={cls}>
              <option value="">—</option>
              <option value="contractor">Contractor delay</option>
              <option value="authority">Government / authority delay</option>
              <option value="property_management">Property management delay</option>
              <option value="client_supplier">Client supplier delay</option>
              <option value="client_decision">Client decision / procurement delay</option>
              <option value="third_party_contractor">Third-party nominated contractor delay</option>
              <option value="force_majeure">Force majeure / public holiday / non-working day</option>
            </select>
          </div>
          <div><label className={lbl}>Delay reason</label><input data-testid="portal-task-edit-delay-reason" value={form.delay_reason} onChange={(e) => setForm((s) => ({ ...s, delay_reason: e.target.value }))} className={cls} placeholder="e.g. Trakhees comments — resubmission" /></div>
          <div><label className={lbl}>Visibility</label>
            <select value={form.visibility} onChange={(e) => setForm((s) => ({ ...s, visibility: e.target.value }))} className={cls}>
              <option value="public">Public (everyone on project)</option>
              <option value="client">Client visible</option>
              <option value="internal">Internal only</option>
            </select>
          </div>
          <div className="sm:col-span-2"><label className={lbl}>Notes</label><textarea rows={2} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} className={cls} /></div>
        </div>
        {err && <p className="text-xs text-[#A3141A] mt-3">{err}</p>}
        <button type="submit" disabled={busy} data-testid="portal-task-edit-submit" className="w-full mt-5 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-60 flex items-center justify-center gap-2">
          <Save size={13} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}

function ImportHistoryPanel({ slug, onRestored }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { const d = await portalApi.importHistory(slug); setItems(d.history || []); }
    catch (e) { setMsg(e.message); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const restore = async (snap) => {
    if (!window.confirm(
      "Restore the programme to the state BEFORE this import?\n\nThis replaces the current phases, tasks, milestones and vendor-package options with the snapshot taken before this import. Daily updates, photos and reports are NOT affected. An audit entry will be recorded."
    )) return;
    setBusy(snap.id); setMsg("");
    try {
      const r = await portalApi.restoreImport(slug, snap.id);
      setMsg(`Restored (${Object.entries(r.restored_counts || {}).map(([k, v]) => `${k}:${v}`).join(", ")}).`);
      await load();
      onRestored && onRestored();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(""); }
  };

  if (items === null) return null;

  return (
    <div className="border border-[#6E1F2C]/15 bg-white" data-testid="portal-import-history">
      <div className="px-4 py-3 border-b border-[#6E1F2C]/10 flex items-center justify-between">
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Import History &amp; Rollback</p>
        {msg && <span className="text-xs text-[#3F6E2C]" data-testid="portal-import-history-msg">{msg}</span>}
      </div>
      {items.length === 0 ? (
        <p className="px-4 py-4 text-xs text-[#7A6B5F]">No programme imports yet. Snapshots appear here after each import.</p>
      ) : (
        <div className="divide-y divide-[#6E1F2C]/10 max-h-[320px] overflow-y-auto">
          {items.map((s) => (
            <div key={s.id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 justify-between" data-testid={`portal-import-history-row-${s.id}`}>
              <div className="min-w-0">
                <p className="text-xs text-[#1C110C]">
                  <span className={`inline-block px-2 py-0.5 mr-2 text-[9px] uppercase tracking-wide ${s.kind === "restore" ? "bg-[#A6822F] text-white" : "bg-[#6E1F2C] text-white"}`}>{s.kind === "restore" ? "Restore" : s.mode}</span>
                  {s.filename || "programme"}
                </p>
                <p className="text-[10px] text-[#7A6B5F] mt-0.5">{s.created_by_name || "—"} · {(s.created_at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={portalApi.changeReportUrl(slug, s.id)}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`portal-import-report-${s.id}`}
                  className="text-[10px] tracking-[0.24em] uppercase border border-[#6E1F2C]/30 text-[#6E1F2C] px-3 py-1.5 hover:bg-[#6E1F2C] hover:text-white"
                >Change report</a>
                <button
                  type="button"
                  onClick={() => restore(s)}
                  disabled={busy === s.id}
                  data-testid={`portal-import-restore-${s.id}`}
                  className="text-[10px] tracking-[0.24em] uppercase border border-[#A3141A]/40 text-[#A3141A] px-3 py-1.5 hover:bg-[#A3141A] hover:text-white disabled:opacity-50"
                >{busy === s.id ? "Restoring…" : "Restore previous"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



export function TimelineTab({ slug, user, project }) {
  const [data, setData] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [importCount, setImportCount] = useState(0);
  const canEdit = user && ["admin", "project_manager", "site_engineer"].includes(user.role);
  const canUpload = user && ["admin", "project_manager"].includes(user.role);

  const reload = useCallback(async () => {
    try {
      const d = await portalApi.getTimeline(slug);
      setData(d);
    } catch (e) {
      setData({ phases: [], tasks: [], milestones: [], external_packages: [], planning_assumptions: [], _error: e.message });
    }
  }, [slug]);

  useEffect(() => { reload(); }, [reload]);

  const tasksByPhase = useMemo(() => {
    if (!data) return { byId: {}, orphan: [] };
    const byId = {};
    (data.phases || []).forEach((p) => { byId[p.id] = []; });
    const orphan = [];
    (data.tasks || []).forEach((t) => {
      if (t.phase_id && byId[t.phase_id]) byId[t.phase_id].push(t);
      else orphan.push(t);
    });
    return { byId, orphan };
  }, [data]);

  const packagesByGroup = useMemo(() => {
    if (!data) return {};
    const g = {};
    (data.external_packages || []).forEach((p) => {
      const k = p.package || "Other";
      (g[k] = g[k] || []).push(p);
    });
    return g;
  }, [data]);

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete task "${t.name}"?`)) return;
    try { await portalApi.deleteTask(slug, t.id); reload(); }
    catch (e) { alert(e.message); }
  };

  if (!data) return <p className="text-sm text-[#7A6B5F]">Loading timeline…</p>;

  const hasAny = (data.phases.length + data.tasks.length + (data.milestones?.length || 0) + data.external_packages.length + data.planning_assumptions.length) > 0;

  // KPI stats
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (data.tasks || []).filter((t) => t.start_date && t.start_date >= today && t.status !== "completed").length;
  const delayedCount = (data.tasks || []).filter((t) => t.status === "delayed").length;
  const highRiskCount = (data.tasks || []).filter((t) => (t.risk || "").toLowerCase() === "high" && t.status !== "completed").length;

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Programme</p>
          <h2 className="font-serif-display text-2xl text-[#1C110C]">Timeline / Gantt</h2>
        </div>
        {canUpload && (
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            data-testid="portal-timeline-upload-btn"
            className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
          >
            <Upload size={13} /> {hasAny ? "Replace programme Excel" : "Upload programme Excel"}
          </button>
        )}
      </div>

      {user && user.role === "admin" && (
        <ImportHistoryPanel key={importCount} slug={slug} onRestored={reload} />
      )}

      {hasAny && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="portal-timeline-kpis">
          {[
            { k: "phases", label: "Phases", count: data.phases.length, icon: Layers, accent: "from-[#6E1F2C] to-[#56151F]" },
            { k: "upcoming", label: "Upcoming", count: upcoming, icon: ChevronRight, accent: "from-[#D4A963] to-[#A6822F]" },
            { k: "delayed", label: "Delayed", count: delayedCount, icon: AlertCircle, accent: "from-[#A3141A] to-[#6E0F14]" },
            { k: "high-risk", label: "High risk", count: highRiskCount, icon: AlertCircle, accent: "from-[#A3141A] to-[#6E0F14]" },
          ].map((s) => {
            const I = s.icon;
            return (
              <div
                key={s.k}
                data-testid={`portal-timeline-kpi-${s.k}`}
                className="relative bg-white border border-[#6E1F2C]/10 p-4 overflow-hidden"
              >
                <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${s.accent}`} />
                <div className="flex items-center justify-between">
                  <I size={14} className="text-[#6E1F2C]" />
                  <p className="font-serif-display text-2xl text-[#1C110C] leading-none">{s.count}</p>
                </div>
                <p className="text-[10px] tracking-[0.26em] uppercase text-[#3E2A22] mt-2">{s.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {!hasAny && (
        <div data-testid="portal-timeline-empty" className="border border-dashed border-[#6E1F2C]/30 p-10 text-center bg-white">
          <FileSpreadsheet className="mx-auto text-[#6E1F2C] mb-3" size={36} />
          <p className="text-sm text-[#3E2A22]">No programme imported yet.</p>
          {canUpload ? (
            <p className="text-xs text-[#7A6B5F] mt-1">Upload your Excel Gantt to populate phases, tasks, package coordination and planning assumptions.</p>
          ) : (
            <p className="text-xs text-[#7A6B5F] mt-1">Your project manager will share the programme shortly.</p>
          )}
        </div>
      )}

      {/* PHASES + TASKS */}
      {data.phases.length > 0 && (
        <section data-testid="portal-timeline-phases">
          <h3 className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mb-3 flex items-center gap-2"><Layers size={12} /> Phases & tasks</h3>
          <div className="space-y-4">
            {data.phases.map((p) => (
              <PhaseCard
                key={p.id} phase={p}
                tasks={tasksByPhase.byId[p.id] || []}
                canEdit={canEdit}
                onEditTask={setEditTask}
                onDeleteTask={deleteTask}
              />
            ))}
            {tasksByPhase.orphan.length > 0 && (
              <PhaseCard
                phase={{ id: "orphan", name: "Unphased tasks" }}
                tasks={tasksByPhase.orphan}
                canEdit={canEdit}
                onEditTask={setEditTask}
                onDeleteTask={deleteTask}
              />
            )}
          </div>
        </section>
      )}

      {data.phases.length === 0 && data.tasks.length > 0 && (
        <section>
          <h3 className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mb-3">Tasks</h3>
          <PhaseCard phase={{ id: "all", name: "All tasks" }} tasks={data.tasks} canEdit={canEdit} onEditTask={setEditTask} onDeleteTask={deleteTask} />
        </section>
      )}

      {/* EXTERNAL PACKAGE TIMELINE */}
      {data.external_packages.length > 0 && (
        <section data-testid="portal-timeline-packages">
          <h3 className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mb-3 flex items-center gap-2"><Package size={12} /> External package timeline</h3>
          <div className="space-y-4">
            {Object.entries(packagesByGroup).map(([pkg, items]) => (
              <div key={pkg} className="border border-[#6E1F2C]/15 bg-white">
                <div className="px-5 py-3 bg-[#FAF5EC] border-b border-[#6E1F2C]/10">
                  <h4 className="text-sm text-[#1C110C]">{pkg}</h4>
                </div>
                <ul className="divide-y divide-[#6E1F2C]/5">
                  {items.map((p) => (
                    <li key={p.id} className="px-5 py-3 flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#1C110C]">{p.activity}</p>
                        <p className="text-[11px] text-[#7A6B5F] mt-0.5">
                          {p.start_date || "—"} → {p.finish_date || "—"}{p.working_days ? ` · ${p.working_days} WD` : ""}
                        </p>
                        {p.trigger && <p className="text-[11px] text-[#3E2A22] mt-1"><strong>Trigger:</strong> {p.trigger}</p>}
                        {p.notes && <p className="text-[11px] text-[#3E2A22] italic mt-1">{p.notes}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {p.status && !canEdit && (
                          <span className={`text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 ${PACKAGE_DONE.has(p.status) ? "bg-emerald-600/15 text-emerald-700" : "bg-[#A6822F]/15 text-[#A6822F]"}`} data-testid={`portal-package-status-${p.id}`}>
                            {PACKAGE_STATUS_LABEL[p.status] || p.status}
                          </span>
                        )}
                        {canEdit && (
                          <select
                            value={p.status || ""}
                            onChange={async (e) => {
                              try {
                                await portalApi.patchExternalPackage(slug, p.id, { status: e.target.value || null });
                                reload();
                              } catch (ex) { window.alert(ex.message); }
                            }}
                            data-testid={`portal-package-status-select-${p.id}`}
                            className={`text-[10px] border px-1.5 py-1 bg-white ${PACKAGE_DONE.has(p.status) ? "border-emerald-600/40 text-emerald-700" : "border-[#6E1F2C]/25 text-[#3E2A22]"}`}
                          >
                            {PACKAGE_STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        )}
                        {p.criticality && <span className={`text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 ${RISK_CHIP[p.criticality?.toLowerCase().includes("high") ? "high" : p.criticality?.toLowerCase().includes("medium") ? "medium" : "low"]}`}>{p.criticality}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PLANNING ASSUMPTIONS — internal only */}
      {data.planning_assumptions.length > 0 && (
        <section data-testid="portal-timeline-assumptions">
          <h3 className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C] mb-3 flex items-center gap-2">
            <Lightbulb size={12} /> Planning assumptions
            <span className="text-[9px] text-[#A3141A] tracking-[0.22em] uppercase">Internal</span>
          </h3>
          <div className="bg-white border border-[#6E1F2C]/15 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAF5EC] text-[#7A6B5F]">
                <tr>
                  <th className="text-left px-4 py-2">Item</th>
                  <th className="text-left px-4 py-2">Date / basis</th>
                  <th className="text-left px-4 py-2">Lead / interface</th>
                  <th className="text-left px-4 py-2">Programme intent</th>
                  <th className="text-left px-4 py-2">Source / note</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.planning_assumptions.map((a) => (
                  <tr key={a.id} className="border-t border-[#6E1F2C]/10 hover:bg-[#FAF5EC]/40">
                    <td className="px-4 py-2 align-top text-[#1C110C]">{a.planning_item}</td>
                    <td className="px-4 py-2 align-top text-[#3E2A22]">{a.date_basis || "—"}</td>
                    <td className="px-4 py-2 align-top text-[#3E2A22]">{a.lead_interface || "—"}</td>
                    <td className="px-4 py-2 align-top text-[#3E2A22]">{a.programme_intent || "—"}</td>
                    <td className="px-4 py-2 align-top text-[#3E2A22]">{a.source_note || "—"}</td>
                    <td className="px-4 py-2 align-top">{a.status ? <span className="text-[9px] tracking-[0.2em] uppercase bg-[#6E1F2C] text-white px-2 py-0.5">{a.status}</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <GanttUploadWizard slug={slug} open={uploadOpen} onClose={() => setUploadOpen(false)} onImported={() => { reload(); setImportCount((c) => c + 1); }} />
      {editTask && (
        <EditTaskModal
          task={editTask} slug={slug}
          onClose={() => setEditTask(null)}
          onSaved={() => reload()}
        />
      )}
    </div>
  );
}
